import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useState,
} from "react";
import {
  BentoSearchFilter,
  Organization,
  OrganizationProfile,
  OrganizationType,
  OrganizationUser,
  OrganizationVaProfile,
  UserIntegration,
  UserNotifications,
} from "schemas/dashboard";
import { EmailSettings } from "schemas/email";

import { OrganizationBulkSendSettings } from "features/Influencer/Discover/Results/DiscoverFilter/AutomationVA/UpcomingPitchScheduleDialog/schema";

/*
 * This is the Context for all Organization related values
 */

interface OrganizationUserContextInterface {
  currentUser: OrganizationUser | null;
  setCurrentUser: React.Dispatch<SetStateAction<OrganizationUser | null>>;
  currentOrg: Organization | null;
  setCurrentOrg: React.Dispatch<SetStateAction<Organization | null>>;
  clearOrganizationContext: () => void;
  isInfluencer: boolean;
  userIntegrations: UserIntegration[];
  setUserIntegrations: React.Dispatch<SetStateAction<UserIntegration[]>>;
  profile: OrganizationProfile | null;
  setProfile: React.Dispatch<SetStateAction<OrganizationProfile>>;
  userNotifications: UserNotifications | null;
  setUserNotifications: React.Dispatch<
    SetStateAction<UserNotifications | null>
  >;
  emailSettings: EmailSettings | null;
  setEmailSettings: React.Dispatch<SetStateAction<EmailSettings | null>>;
  hideCityDialog: boolean;
  setHideCityDialog: React.Dispatch<SetStateAction<boolean>>;
  includedBentoSearchFilterIds: number[];
  setIncludedBentoSearchFilterIds: React.Dispatch<SetStateAction<number[]>>;
  excludedBentoSearchFilterIds: number[];
  setExcludedBentoSearchFilterIds: React.Dispatch<SetStateAction<number[]>>;
  searchFilters: BentoSearchFilter[];
  setSearchFilters: Dispatch<SetStateAction<BentoSearchFilter[]>>;
  organizationVaProfile: OrganizationVaProfile | null;
  setOrganizationVaProfile: Dispatch<
    SetStateAction<OrganizationVaProfile | null>
  >;
  organizationBulkSendSettings: OrganizationBulkSendSettings | null;
  setOrganizationBulkSendSettings: Dispatch<
    SetStateAction<OrganizationBulkSendSettings | null>
  >;
  totalEmailsSent: number;
  setTotalEmailsSent: React.Dispatch<SetStateAction<number>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  currentUser: null,
  setCurrentUser: defaultContextMissingFunction,
  currentOrg: null,
  setCurrentOrg: defaultContextMissingFunction,
  clearOrganizationContext: defaultContextMissingFunction,
  isInfluencer: false,
  userIntegrations: [],
  setUserIntegrations: defaultContextMissingFunction,
  profile: null,
  setProfile: defaultContextMissingFunction,
  userNotifications: null,
  setUserNotifications: defaultContextMissingFunction,
  emailSettings: null,
  setEmailSettings: defaultContextMissingFunction,
  hideCityDialog: false,
  setHideCityDialog: defaultContextMissingFunction,
  includedBentoSearchFilterIds: [],
  setIncludedBentoSearchFilterIds: defaultContextMissingFunction,
  excludedBentoSearchFilterIds: [],
  setExcludedBentoSearchFilterIds: defaultContextMissingFunction,
  searchFilters: [],
  setSearchFilters: defaultContextMissingFunction,
  organizationVaProfile: null,
  setOrganizationVaProfile: defaultContextMissingFunction,
  organizationBulkSendSettings: null,
  setOrganizationBulkSendSettings: defaultContextMissingFunction,
  totalEmailsSent: 0,
  setTotalEmailsSent: defaultContextMissingFunction,
};

const OrganizationUserContext =
  createContext<OrganizationUserContextInterface>(defaultInterface);

interface OrganizationUserProviderProps {
  children: React.ReactNode;
}

const OrganizationUserProvider = ({
  children,
}: OrganizationUserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<OrganizationUser | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [profile, setProfile] = useState<OrganizationProfile>({
    id: 0,
  });
  const [userNotifications, setUserNotifications] =
    useState<UserNotifications | null>(null);

  const isInfluencer = currentOrg?.type === OrganizationType.INFLUENCER;

  const [userIntegrations, setUserIntegrations] = useState<UserIntegration[]>(
    [],
  );
  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(
    null,
  );
  const [hideCityDialog, setHideCityDialog] = useState<boolean>(false);
  const [includedBentoSearchFilterIds, setIncludedBentoSearchFilterIds] =
    useState<number[]>([]);
  const [excludedBentoSearchFilterIds, setExcludedBentoSearchFilterIds] =
    useState<number[]>([]);
  const [searchFilters, setSearchFilters] = useState<BentoSearchFilter[]>([]);
  const [organizationVaProfile, setOrganizationVaProfile] =
    useState<OrganizationVaProfile | null>(null);
  const [organizationBulkSendSettings, setOrganizationBulkSendSettings] =
    useState<OrganizationBulkSendSettings | null>(null);
  const [totalEmailsSent, setTotalEmailsSent] = useState<number>(0);

  const clearOrganizationContext = () => {
    setCurrentUser(null);
    setCurrentOrg(null);
    setHideCityDialog(false);
  };

  return (
    <OrganizationUserContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        currentOrg,
        setCurrentOrg,
        clearOrganizationContext,
        isInfluencer,
        userIntegrations,
        setUserIntegrations,
        profile,
        setProfile,
        userNotifications,
        setUserNotifications,
        emailSettings,
        setEmailSettings,
        hideCityDialog,
        setHideCityDialog,
        includedBentoSearchFilterIds,
        setIncludedBentoSearchFilterIds,
        excludedBentoSearchFilterIds,
        setExcludedBentoSearchFilterIds,
        searchFilters,
        setSearchFilters,
        organizationVaProfile,
        setOrganizationVaProfile,
        organizationBulkSendSettings,
        setOrganizationBulkSendSettings,
        totalEmailsSent,
        setTotalEmailsSent,
      }}
    >
      {children}
    </OrganizationUserContext.Provider>
  );
};

export { OrganizationUserProvider, OrganizationUserContext };
